import { dragScroll } from './global.js';

/**
 * <product-carousel>
 * Le defilement est natif : `overflow-x` + `scroll-snap` sur le rail. Le
 * doigt, le trackpad et le clavier fonctionnent donc sans une ligne de
 * JavaScript. Ce module ajoute par-dessus les fleches, le compteur de
 * position, et le glisser a la souris via `dragScroll`.
 */

class ProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.pager = this.querySelector('[data-carousel-pager]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.indexLabel = this.querySelector('[data-carousel-index]');

    this.items = [...this.track.querySelectorAll('.rc-carousel__item')];
    if (!this.overflows()) return;

    if (this.pager) this.pager.hidden = false;
    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));

    this.track.addEventListener('scroll', () => this.sync(), { passive: true });

    this.releaseDrag = dragScroll(this.track);

    this.resizeObserver = new ResizeObserver(() => {
      if (this.pager) this.pager.hidden = !this.overflows();
      this.sync();
    });
    this.resizeObserver.observe(this.track);

    this.sync();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.releaseDrag?.();
  }

  overflows() {
    return this.track.scrollWidth - this.track.clientWidth > 4;
  }

  /* Le defilement a deux doigts vers la gauche ou la droite est gere
     nativement par `overflow-x`, sans code. La molette verticale, elle,
     n'est volontairement pas detournee : la capturer vole le defilement de
     la page des que le curseur passe sur le rail, et le visiteur se retrouve
     bloque au milieu de la home sans comprendre pourquoi. */

  /** Avance d'une vignette : le repere visuel reste, on ne saute pas une page. */
  step(direction) {
    const width = this.itemWidth();
    this.track.scrollBy({ left: width * direction });
  }

  itemWidth() {
    const first = this.items[0];
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  sync() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const left = this.track.scrollLeft;

    if (this.prev) this.prev.disabled = left <= 2;
    if (this.next) this.next.disabled = left >= max - 2;

    if (this.indexLabel) {
      const index = Math.round(left / this.itemWidth()) + 1;
      this.indexLabel.textContent = Math.min(index, this.items.length);
    }
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel);
}
